import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';

const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024;
const MAX_RETRIES = 3;

function putChunk(
  uploadUrl: string,
  chunk: Blob,
  start: number,
  total: number,
  signal: AbortSignal,
  onProgress: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    request.open('PUT', uploadUrl);
    request.setRequestHeader('Content-Range', `bytes ${start}-${start + chunk.size - 1}/${total}`);
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    request.upload.onprogress = (event) => onProgress(event.loaded);
    request.onload = () => {
      signal.removeEventListener('abort', abort);
      if ([200, 201, 202].includes(request.status)) resolve();
      else reject(new Error(`Microsoft rechazó el fragmento (${request.status}).`));
    };
    request.onerror = () => {
      signal.removeEventListener('abort', abort);
      reject(new Error('Se perdió la conexión durante la carga.'));
    };
    request.onabort = () => {
      signal.removeEventListener('abort', abort);
      reject(new DOMException('Carga cancelada.', 'AbortError'));
    };
    request.send(chunk);
  });
}

export async function uploadFileDirectly(
  file: File,
  uploadUrl: string,
  options: {
    chunkSizeBytes?: number;
    signal: AbortSignal;
    onProgress: (percent: number, uploadedBytes: number) => void;
  },
): Promise<{ checksumSha256: string }> {
  const chunkSize = options.chunkSizeBytes ?? DEFAULT_CHUNK_SIZE;
  const hasher = sha256.create();
  let completedBytes = 0;

  for (let start = 0; start < file.size; start += chunkSize) {
    if (options.signal.aborted) throw new DOMException('Carga cancelada.', 'AbortError');
    const chunk = file.slice(start, Math.min(start + chunkSize, file.size));
    hasher.update(new Uint8Array(await chunk.arrayBuffer()));
    let attempt = 0;
    while (true) {
      try {
        await putChunk(uploadUrl, chunk, start, file.size, options.signal, (loaded) => {
          const uploaded = completedBytes + loaded;
          options.onProgress(Math.min(100, Math.round((uploaded / file.size) * 100)), uploaded);
        });
        break;
      } catch (error) {
        if (options.signal.aborted || error instanceof DOMException && error.name === 'AbortError') throw error;
        attempt += 1;
        if (attempt > MAX_RETRIES) throw error;
        await new Promise((resolve) => window.setTimeout(resolve, 500 * 2 ** attempt));
      }
    }
    completedBytes += chunk.size;
    options.onProgress(Math.round((completedBytes / file.size) * 100), completedBytes);
  }

  return { checksumSha256: bytesToHex(hasher.digest()) };
}
